/**
 * Minimal i18n for the portal SPA — pt-BR primary, English available.
 *
 * No i18n library: a flat string dictionary per locale, a context, and a
 * `t(key, vars?)` helper with `{var}` interpolation. The chosen locale
 * persists in localStorage. pt-BR is the default per the PRD.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type Locale = "pt-BR" | "en";

const STORAGE_KEY = "finn_portal_locale";
export const CONTACT_EMAIL = "contato@aton-ai.com";

type Dict = Record<string, string>;

const STRINGS: Record<Locale, Dict> = {
  "pt-BR": {
    "lang.pt": "Português",
    "lang.en": "English",

    "footer.privacy": "Privacidade",
    "footer.terms": "Termos",
    "footer.install": "Guia de instalação",

    "landing.tagline": "Finn — seu colega financeiro no Teams",
    "landing.body":
      "O Finn responde perguntas sobre notas, boletos e fornecedores " +
      "diretamente no Microsoft Teams. Ele é feito pela Aton para clientes " +
      "do RecebeMais.",
    "landing.inviteOnly":
      "O acesso ao Finn é por convite. Se a sua organização ainda não foi " +
      "liberada, fale com a Aton em {email} para começar.",
    "landing.signIn": "Entrar com a Microsoft",

    "wizard.validating": "Verificando o seu convite…",
    "wizard.signingIn": "Concluindo o login…",
    "wizard.confirming": "Concluindo a configuração…",
    "wizard.downloading": "Preparando o download…",

    "signin.title": "Boas-vindas ao Finn",
    "signin.body":
      "Entre com a sua conta Microsoft para concluir a configuração do " +
      "Finn na sua organização. O login confirma a sua identidade e " +
      "identifica automaticamente o tenant do Microsoft 365 da empresa.",
    "signin.adminNote":
      "Use uma conta com permissão de administrador do Microsoft 365 / " +
      "Teams — ela será necessária para instalar o Finn.",
    "signin.button": "Entrar com a Microsoft",

    "problem.expired.title": "Este convite expirou",
    "problem.expired.body":
      "Os links de convite valem por 14 dias. Peça à Aton um novo link " +
      "para continuar.",
    "problem.revoked.title": "Este convite não é mais válido",
    "problem.revoked.body":
      "Este convite foi cancelado. Peça à Aton um novo link para continuar.",
    "problem.notFound.title": "Convite não encontrado",
    "problem.notFound.body":
      "Confira se você abriu o link completo enviado pela Aton. Se o " +
      "problema continuar, peça um novo link.",
    "problem.alreadyUsed.title": "Este convite já foi utilizado",
    "problem.alreadyUsed.body":
      "A configuração do Finn para esta organização já foi iniciada. " +
      "Se precisar de ajuda, fale com a Aton.",
    "problem.contact": "Fale com a Aton em {email}.",

    "mismatch.title": "Conta incorreta",
    "mismatch.body":
      "Este convite é para {invited}, mas você entrou como {signed}. " +
      "Saia e entre novamente com a conta convidada.",
    "mismatch.switch": "Trocar de conta",

    "confirm.title": "Confirme os dados da empresa",
    "confirm.intro":
      "Revise as informações abaixo antes de concluir a configuração do " +
      "Finn na sua organização.",
    "confirm.company": "Empresa",
    "confirm.tenant": "O Finn vai funcionar em",
    "confirm.signedInAs": "Conectado como",
    "confirm.tenantNote":
      "O ambiente do Microsoft 365 foi identificado automaticamente pela " +
      "sua conta — ele não pode ser alterado aqui.",
    "confirm.button": "Confirmar e continuar",
    "confirm.wrong": "Algum dado está errado? Fale com a Aton",

    "flagWrong.title": "Vamos verificar os seus dados",
    "flagWrong.body":
      "Sem problema. Fale com a Aton para corrigir os dados da sua empresa " +
      "antes de continuar. Nada foi vinculado ainda.",

    "conflict.title": "Este ambiente já está em uso",
    "conflict.body":
      "O ambiente do Microsoft 365 da sua conta já está vinculado a outra " +
      "organização no Finn. Fale com a Aton para resolver.",

    "download.title": "Baixe o Finn para o Teams",
    "download.body":
      "A {company} está configurada. Baixe o pacote do aplicativo e " +
      "encaminhe ao administrador de TI para instalar o Finn no " +
      "Microsoft Teams.",
    "download.button": "Baixar finn.zip",
    "download.guide": "Ver o guia de instalação",

    "allset.title": "Tudo pronto!",
    "allset.body":
      "O Finn está configurado para a sua organização. Você já pode " +
      "fechar esta página — o administrador de TI conclui a instalação " +
      "seguindo o guia.",
    "allset.guide": "Abrir o guia de instalação",
    "allset.continue": "Ir para a minha conta",

    "error.title": "Algo deu errado",
    "error.body":
      "Não foi possível concluir agora. Tente novamente em instantes. " +
      "Se o problema continuar, fale com a Aton.",
    "error.retry": "Tentar novamente",

    "sidebar.account": "Conta",
    "sidebar.knowledge": "Conhecimento",
    "sidebar.mcp": "Integrações MCP",
    "sidebar.signOut": "Sair",

    "mcp.title": "Servidores MCP",
    "mcp.subtitle":
      "Integrações com seus sistemas internos. Você pode pausar, " +
      "atualizar a chave de acesso ou remover a qualquer momento. " +
      "Novas integrações são cadastradas pela equipe da Aton.",
    "mcp.empty.title": "Nenhuma integração configurada",
    "mcp.empty.body":
      "A equipe da Aton ainda não cadastrou nenhuma integração MCP " +
      "para a sua organização. Fale com a Aton para começar.",
    "mcp.card.calls24h": "{n} chamadas / 24h",
    "mcp.card.errorRate": "Taxa de erro: {pct}%",
    "mcp.card.latency": "Latência p50/p95: {p50} / {p95} ms",
    "mcp.card.noTraffic": "Sem chamadas nas últimas 24h",
    "mcp.card.open": "Abrir",
    "mcp.metric.calls": "Chamadas",
    "mcp.metric.errors": "Erros",
    "mcp.metric.latencyP95": "Latência p95",
    "mcp.status.pending_iam": "Aguardando IAM",
    "mcp.status.pending_probe": "Aguardando sondagem",
    "mcp.status.pending_approval": "Aguardando aprovação",
    "mcp.status.ready": "Pronto",
    "mcp.status.disabled": "Desativado",
    "mcp.status.auth_failed": "Falha de autenticação",
    "mcp.status.degraded": "Degradado",
    "mcp.detail.back": "Voltar",
    "mcp.detail.tools": "Ferramentas aprovadas",
    "mcp.detail.toolCalls7d": "{n} chamadas / 7d",
    "mcp.detail.toolErrors7d": "{n} erros / 7d",
    "mcp.detail.toolDisabled": "Desativada",
    "mcp.detail.inputSchema": "Schema de entrada",
    "mcp.detail.outputSchema": "Schema de saída",
    "mcp.action.disable": "Pausar",
    "mcp.action.rotate": "Atualizar token",
    "mcp.action.reprobe": "Sondar novamente",
    "mcp.action.delete": "Remover",
    "mcp.action.cancel": "Cancelar",
    "mcp.disable.title": "Pausar esta integração?",
    "mcp.disable.body":
      "As respostas do Finn deixarão de usar dados desta integração. " +
      "A equipe da Aton precisa reativá-la.",
    "mcp.disable.confirm": "Pausar",
    "mcp.delete.title": "Remover esta integração?",
    "mcp.delete.body":
      "A integração será removida e a chave de acesso será apagada do " +
      "cofre. Esta ação não pode ser desfeita por aqui.",
    "mcp.delete.confirm": "Remover",
    "mcp.rotate.title": "Atualizar token de acesso",
    "mcp.rotate.body":
      "Cole o novo token. Ele substitui imediatamente o valor anterior; " +
      "nenhuma alteração de política IAM é necessária.",
    "mcp.rotate.placeholder": "Cole o novo token aqui",
    "mcp.rotate.confirm": "Atualizar token",
    "mcp.reprobe.title": "Sondar a integração novamente?",
    "mcp.reprobe.body":
      "O Finn vai chamar o servidor MCP e verificar se a lista de " +
      "ferramentas mudou. Marque \"urgente\" se precisar que a equipe da " +
      "Aton revise hoje.",
    "mcp.reprobe.urgent": "Marcar como urgente (avisa a Aton)",
    "mcp.reprobe.confirm": "Sondar",
    "mcp.reprobe.done":
      "Sondagem concluída. Alterações encontradas: {a} aplicadas, {b} " +
      "aguardando sua aprovação, {c} aguardando revisão da Aton.",
    "mcp.error":
      "Não foi possível executar agora. Tente novamente em instantes.",

    "account.title": "Conta",
    "account.sectionTitle": "Dados da conta",
    "account.activatedOn": "Finn ativado em",
    "account.company": "Empresa",
    "account.signedInAs": "Conectado como",
    "account.loading": "Carregando…",
    "account.pendingFirstUse.label": "Aguardando primeiro uso",
    "account.pendingFirstUse.body":
      "O Finn está configurado, mas ainda não foi usado. Abra o " +
      "Microsoft Teams e diga “oi” ao Finn para ativá-lo.",

    "notOnboarded.title": "A sua empresa ainda não está no Finn",
    "notOnboarded.body":
      "O acesso ao Finn precisa ser liberado pela Aton para a sua " +
      "organização. Fale com a Aton para começar.",

    "knowledge.title": "Conhecimento",
    "knowledge.subtitle":
      "Envie políticas internas em PDF, DOCX ou texto. O Finn vai " +
      "responder no Teams citando trechos com link para a fonte.",
    "knowledge.quota": "{used} de {max} documentos",
    "knowledge.cta": "Enviar documento",
    "knowledge.storageLabel": "Armazenamento",
    "knowledge.recentDocs": "Documentos recentes",

    "knowledge.forbidden.title": "Acesso restrito",
    "knowledge.forbidden.body":
      "Só o administrador que aceitou o convite pode gerenciar a base de " +
      "conhecimento. Fale com a pessoa que concluiu a configuração do Finn.",

    "knowledge.empty.title": "Comece a ensinar o Finn",
    "knowledge.empty.body":
      "Envie políticas internas para que o Finn responda perguntas " +
      "operacionais no Teams citando o documento de origem.",
    "knowledge.empty.hintsTitle": "O que funciona melhor:",
    "knowledge.empty.hint1": "matrizes de aprovação e alçadas",
    "knowledge.empty.hint2": "SOPs de cadastro e onboarding de fornecedores",
    "knowledge.empty.hint3": "memorandos de conformidade fiscal",
    "knowledge.empty.hint4": "políticas de pagamento e antecipação",
    "knowledge.empty.cta": "Enviar o primeiro documento",

    "knowledge.upload.title": "Enviar documentos",
    "knowledge.upload.zonePrompt": "Clique ou arraste arquivos aqui",
    "knowledge.upload.zoneHint": "PDF, DOCX, MD, TXT · Máx 25 MB",
    "knowledge.upload.dropPrompt": "Arraste arquivos aqui ou",
    "knowledge.upload.browse": "selecione do seu computador",
    "knowledge.upload.accepts": "PDF, DOCX, Markdown ou TXT — até 25 MB cada.",
    "knowledge.upload.start": "Iniciar envio",
    "knowledge.upload.cancel": "Cancelar",
    "knowledge.upload.close": "Fechar",
    "knowledge.upload.queued": "Selecionados:",
    "knowledge.upload.removeFile": "Remover",
    "knowledge.upload.invalidType":
      "Formato não suportado. Aceitos: PDF, DOCX, MD, TXT.",
    "knowledge.upload.tooBig": "Arquivo acima de 25 MB.",
    "knowledge.upload.collision":
      "Já existe um documento com o nome “{filename}”. Um novo " +
      "documento será criado — o anterior continua disponível.",
    "knowledge.upload.allDone": "Envio concluído.",
    "knowledge.upload.someFailed":
      "Alguns envios falharam — verifique a mensagem em cada arquivo.",
    "knowledge.upload.sending": "Enviando…",
    "knowledge.upload.sent": "Enviado",
    "knowledge.upload.failed": "Falhou",

    "knowledge.consent.title": "Antes de enviar pela primeira vez",
    "knowledge.consent.body":
      "Os documentos enviados ficam armazenados na nuvem da Aton em São " +
      "Paulo (OCI), e o conteúdo é convertido em embeddings via OpenAI " +
      "para responder às perguntas no Teams. Não compartilhe documentos " +
      "que você não tenha autorização para usar.",
    "knowledge.consent.accept": "Concordo e continuar",
    "knowledge.consent.cancel": "Cancelar",

    "knowledge.status.pending": "Pendente",
    "knowledge.status.embedding": "Processando",
    "knowledge.status.ready": "Pronto",
    "knowledge.status.failed": "Falhou",

    "knowledge.card.pages": "{count} páginas",
    "knowledge.card.chunks": "{count} trechos",
    "knowledge.card.age.today": "hoje",
    "knowledge.card.age.daysAgo": "há {n} dias",
    "knowledge.card.delete": "Excluir",

    "knowledge.delete.title": "Excluir documento?",
    "knowledge.delete.body":
      "O documento “{filename}” será removido das respostas do Finn. " +
      "Esta ação não pode ser desfeita pela tela.",
    "knowledge.delete.confirm": "Excluir",
    "knowledge.delete.cancel": "Cancelar",

    "knowledge.loadError":
      "Não foi possível carregar a lista de documentos. Tente novamente em instantes.",
  },
  en: {
    "lang.pt": "Português",
    "lang.en": "English",

    "footer.privacy": "Privacy",
    "footer.terms": "Terms",
    "footer.install": "Install guide",

    "landing.tagline": "Finn — your financial coworker in Teams",
    "landing.body":
      "Finn answers questions about invoices, bills, and suppliers right " +
      "inside Microsoft Teams. It is built by Aton for RecebeMais customers.",
    "landing.inviteOnly":
      "Access to Finn is invite-only. If your organization has not been " +
      "onboarded yet, contact Aton at {email} to get started.",
    "landing.signIn": "Sign in with Microsoft",

    "wizard.validating": "Checking your invitation…",
    "wizard.signingIn": "Finishing sign-in…",
    "wizard.confirming": "Finishing setup…",
    "wizard.downloading": "Preparing your download…",

    "signin.title": "Welcome to Finn",
    "signin.body":
      "Sign in with your Microsoft account to finish setting Finn up for " +
      "your organization. Signing in confirms your identity and " +
      "automatically detects your company's Microsoft 365 tenant.",
    "signin.adminNote":
      "Use an account with Microsoft 365 / Teams admin rights — they are " +
      "needed to install Finn.",
    "signin.button": "Sign in with Microsoft",

    "problem.expired.title": "This invitation has expired",
    "problem.expired.body":
      "Invitation links are valid for 14 days. Please ask Aton for a new " +
      "link to continue.",
    "problem.revoked.title": "This invitation is no longer valid",
    "problem.revoked.body":
      "This invitation was cancelled. Please ask Aton for a new link to " +
      "continue.",
    "problem.notFound.title": "Invitation not found",
    "problem.notFound.body":
      "Check that you opened the full link Aton sent you. If the problem " +
      "persists, ask for a new link.",
    "problem.alreadyUsed.title": "This invitation has already been used",
    "problem.alreadyUsed.body":
      "Setup of Finn for this organization has already started. Contact " +
      "Aton if you need help.",
    "problem.contact": "Contact Aton at {email}.",

    "mismatch.title": "Wrong account",
    "mismatch.body":
      "This invitation is for {invited}, but you signed in as {signed}. " +
      "Please sign out and sign in with the invited account.",
    "mismatch.switch": "Switch account",

    "confirm.title": "Confirm your company details",
    "confirm.intro":
      "Review the details below before finishing Finn's setup for your " +
      "organization.",
    "confirm.company": "Company",
    "confirm.tenant": "Finn will run in",
    "confirm.signedInAs": "Signed in as",
    "confirm.tenantNote":
      "Your Microsoft 365 environment was detected automatically from " +
      "your account — it can't be changed here.",
    "confirm.button": "Confirm and continue",
    "confirm.wrong": "Something looks wrong? Contact Aton",

    "flagWrong.title": "Let's check your details",
    "flagWrong.body":
      "No problem. Contact Aton to correct your company details before " +
      "continuing. Nothing has been linked yet.",

    "conflict.title": "This environment is already in use",
    "conflict.body":
      "The Microsoft 365 environment for your account is already linked " +
      "to another organization in Finn. Contact Aton to sort this out.",

    "download.title": "Download Finn for Teams",
    "download.body":
      "{company} is set up. Download the app package and hand it to your " +
      "IT admin to install Finn in Microsoft Teams.",
    "download.button": "Download finn.zip",
    "download.guide": "View the install guide",

    "allset.title": "You're all set!",
    "allset.body":
      "Finn is set up for your organization. You can close this page now " +
      "— your IT admin finishes the installation by following the guide.",
    "allset.guide": "Open the install guide",
    "allset.continue": "Go to your account",

    "error.title": "Something went wrong",
    "error.body":
      "We couldn't finish just now. Please try again shortly. If the " +
      "problem persists, contact Aton.",
    "error.retry": "Try again",

    "sidebar.account": "Account",
    "sidebar.knowledge": "Knowledge",
    "sidebar.mcp": "MCP integrations",
    "sidebar.signOut": "Sign out",

    "mcp.title": "MCP servers",
    "mcp.subtitle":
      "Integrations with your internal systems. You can pause one, " +
      "update its access token, or remove it at any time. New " +
      "integrations are set up by the Aton team.",
    "mcp.empty.title": "No integrations configured",
    "mcp.empty.body":
      "The Aton team has not registered any MCP integration for " +
      "your organization yet. Contact Aton to get started.",
    "mcp.card.calls24h": "{n} calls / 24h",
    "mcp.card.errorRate": "Error rate: {pct}%",
    "mcp.card.latency": "Latency p50/p95: {p50} / {p95} ms",
    "mcp.card.noTraffic": "No calls in the last 24h",
    "mcp.card.open": "Open",
    "mcp.metric.calls": "Calls",
    "mcp.metric.errors": "Errors",
    "mcp.metric.latencyP95": "Latency p95",
    "mcp.status.pending_iam": "Pending IAM",
    "mcp.status.pending_probe": "Pending probe",
    "mcp.status.pending_approval": "Pending approval",
    "mcp.status.ready": "Ready",
    "mcp.status.disabled": "Disabled",
    "mcp.status.auth_failed": "Auth failed",
    "mcp.status.degraded": "Degraded",
    "mcp.detail.back": "Back",
    "mcp.detail.tools": "Approved tools",
    "mcp.detail.toolCalls7d": "{n} calls / 7d",
    "mcp.detail.toolErrors7d": "{n} errors / 7d",
    "mcp.detail.toolDisabled": "Disabled",
    "mcp.detail.inputSchema": "Input schema",
    "mcp.detail.outputSchema": "Output schema",
    "mcp.action.disable": "Pause",
    "mcp.action.rotate": "Rotate token",
    "mcp.action.reprobe": "Re-probe",
    "mcp.action.delete": "Remove",
    "mcp.action.cancel": "Cancel",
    "mcp.disable.title": "Pause this integration?",
    "mcp.disable.body":
      "Finn answers will stop using this integration's data. The Aton " +
      "team has to re-enable it for you.",
    "mcp.disable.confirm": "Pause",
    "mcp.delete.title": "Remove this integration?",
    "mcp.delete.body":
      "The integration will be removed and its access token will be " +
      "deleted from the vault. This cannot be undone from this page.",
    "mcp.delete.confirm": "Remove",
    "mcp.rotate.title": "Rotate access token",
    "mcp.rotate.body":
      "Paste the new token. It replaces the previous value immediately; " +
      "no IAM policy change is needed.",
    "mcp.rotate.placeholder": "Paste new token here",
    "mcp.rotate.confirm": "Rotate token",
    "mcp.reprobe.title": "Re-probe the integration?",
    "mcp.reprobe.body":
      "Finn will call the MCP server and check if the tool list " +
      "changed. Mark as urgent if you need the Aton team to review " +
      "today.",
    "mcp.reprobe.urgent": "Mark as urgent (pages Aton)",
    "mcp.reprobe.confirm": "Re-probe",
    "mcp.reprobe.done":
      "Re-probe finished. Changes found: {a} applied, {b} awaiting " +
      "your approval, {c} awaiting Aton review.",
    "mcp.error":
      "We couldn't run that just now. Please try again shortly.",

    "account.title": "Account",
    "account.sectionTitle": "Account details",
    "account.activatedOn": "Finn activated on",
    "account.company": "Company",
    "account.signedInAs": "Signed in as",
    "account.loading": "Loading…",
    "account.pendingFirstUse.label": "Pending first use",
    "account.pendingFirstUse.body":
      "Finn is set up but hasn't been used yet. Open Microsoft Teams " +
      "and say “hi” to Finn to activate it.",

    "notOnboarded.title": "Your company isn't on Finn yet",
    "notOnboarded.body":
      "Access to Finn needs to be enabled by Aton for your " +
      "organization. Contact Aton to get started.",

    "knowledge.title": "Knowledge",
    "knowledge.subtitle":
      "Upload internal policies as PDF, DOCX, or text. Finn will answer " +
      "in Teams citing passages with a link back to the source.",
    "knowledge.quota": "{used} of {max} documents",
    "knowledge.cta": "Upload document",
    "knowledge.storageLabel": "Storage",
    "knowledge.recentDocs": "Recent documents",

    "knowledge.forbidden.title": "Restricted access",
    "knowledge.forbidden.body":
      "Only the admin who accepted the invitation can manage the " +
      "knowledge base. Please contact the person who completed Finn's setup.",

    "knowledge.empty.title": "Start teaching Finn",
    "knowledge.empty.body":
      "Upload internal policies so Finn can answer operational questions " +
      "in Teams citing the source document.",
    "knowledge.empty.hintsTitle": "Works best with:",
    "knowledge.empty.hint1": "approval matrices and limits",
    "knowledge.empty.hint2": "vendor onboarding SOPs",
    "knowledge.empty.hint3": "tax compliance memos",
    "knowledge.empty.hint4": "payment and advance policies",
    "knowledge.empty.cta": "Upload your first document",

    "knowledge.upload.title": "Upload documents",
    "knowledge.upload.zonePrompt": "Click or drag files here",
    "knowledge.upload.zoneHint": "PDF, DOCX, MD, TXT · Max 25 MB",
    "knowledge.upload.dropPrompt": "Drag files here or",
    "knowledge.upload.browse": "browse your computer",
    "knowledge.upload.accepts": "PDF, DOCX, Markdown, or TXT — up to 25 MB each.",
    "knowledge.upload.start": "Start upload",
    "knowledge.upload.cancel": "Cancel",
    "knowledge.upload.close": "Close",
    "knowledge.upload.queued": "Selected:",
    "knowledge.upload.removeFile": "Remove",
    "knowledge.upload.invalidType":
      "Unsupported format. Accepted: PDF, DOCX, MD, TXT.",
    "knowledge.upload.tooBig": "File larger than 25 MB.",
    "knowledge.upload.collision":
      "A document named “{filename}” already exists. A new document " +
      "will be created — the previous one stays available.",
    "knowledge.upload.allDone": "Upload complete.",
    "knowledge.upload.someFailed":
      "Some uploads failed — see the message on each file.",
    "knowledge.upload.sending": "Sending…",
    "knowledge.upload.sent": "Sent",
    "knowledge.upload.failed": "Failed",

    "knowledge.consent.title": "Before your first upload",
    "knowledge.consent.body":
      "Uploaded documents are stored in Aton's São Paulo cloud (OCI), " +
      "and their contents are turned into embeddings via OpenAI to " +
      "answer questions in Teams. Do not upload documents you are not " +
      "authorized to share.",
    "knowledge.consent.accept": "I agree, continue",
    "knowledge.consent.cancel": "Cancel",

    "knowledge.status.pending": "Pending",
    "knowledge.status.embedding": "Processing",
    "knowledge.status.ready": "Ready",
    "knowledge.status.failed": "Failed",

    "knowledge.card.pages": "{count} pages",
    "knowledge.card.chunks": "{count} chunks",
    "knowledge.card.age.today": "today",
    "knowledge.card.age.daysAgo": "{n} days ago",
    "knowledge.card.delete": "Delete",

    "knowledge.delete.title": "Delete document?",
    "knowledge.delete.body":
      "The document “{filename}” will be removed from Finn's answers. " +
      "This action cannot be undone from this page.",
    "knowledge.delete.confirm": "Delete",
    "knowledge.delete.cancel": "Cancel",

    "knowledge.loadError":
      "We couldn't load the documents list. Please try again shortly.",
  },
};

function detectInitialLocale(): Locale {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved === "en" || saved === "pt-BR") return saved;
  // pt-BR is the primary locale; only an explicitly English browser
  // starts in English.
  const nav = navigator.language?.toLowerCase() ?? "";
  return nav.startsWith("pt") ? "pt-BR" : nav.startsWith("en") ? "en" : "pt-BR";
}

type I18n = {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, vars?: Record<string, string>) => string;
};

const I18nContext = createContext<I18n | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectInitialLocale);

  const setLocale = useCallback((l: Locale) => {
    localStorage.setItem(STORAGE_KEY, l);
    setLocaleState(l);
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string>) => {
      const template = STRINGS[locale][key] ?? STRINGS["pt-BR"][key] ?? key;
      if (!vars) return template;
      return template.replace(/\{(\w+)\}/g, (_m, name) => vars[name] ?? `{${name}}`);
    },
    [locale],
  );

  const value = useMemo<I18n>(
    () => ({ locale, setLocale, t }),
    [locale, setLocale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const v = useContext(I18nContext);
  if (!v) throw new Error("useI18n must be used within an I18nProvider");
  return v;
}
